/**
 * Shared design tokens for the dashboard visualisations
 * (Coordinator Workload, Jobs by Status, Top Clients by Volume, Job Health by
 * Coordinator, Needs Attention, and the KPI Performance charts).
 *
 * Values mirror `docs/style-guide/design-system.html` so a later adoption of
 * `restorize-app.css` is a mechanical swap. Consume the token values, not the
 * guide's classes — the app does not load that stylesheet yet.
 */
export const chartTokens = {
  // --rz-blue / --rz-blue-050
  accent: '#3164E1',
  accentSoft: '#E9EDFD',
  accentMuted: '#EBF1FE',
  // --app-surface-sel
  track: '#EBF1FE',
  // --app-border
  cardBorder: '#E3E9F2',
  divider: '#EEF1F7',
  // --app-text / --app-text-2 / --app-text-3
  heading: '#0A2540',
  body: '#41546F',
  muted: '#8693A5',
  columnHeader: '#8693A5',
  marker: '#5A6485',
  // --r-lg
  radius: 12,
};

export const cardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: `${chartTokens.radius}px`,
  border: `1px solid ${chartTokens.cardBorder}`,
  boxShadow: 'none',
};

export const titleSx = {
  fontSize: 20,
  fontWeight: 700,
  color: chartTokens.heading,
  lineHeight: 1.25,
};

export const subtitleSx = {
  fontSize: 13,
  color: chartTokens.muted,
  marginTop: '2px',
};

export const columnHeaderSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: chartTokens.columnHeader,
};

export const footerSx = {
  fontSize: 12.5,
  color: chartTokens.muted,
};

/**
 * Tabular numerals for any ranked/count display. The design system reserves
 * `.rz-num` for this; until the stylesheet lands the style is applied inline.
 * Ranked lists of numbers are exactly the case it exists for — without it a
 * single-digit count shifts the column a pixel and the eye reads it as noise.
 */
export const tabularNumSx = {
  fontVariantNumeric: 'tabular-nums' as const,
};

/** Pill shaped `Select` used for the range filters in the card headers. */
export const pillSelectSx = {
  borderRadius: '999px',
  height: 36,
  fontSize: 14,
  fontWeight: 500,
  color: chartTokens.heading,
  backgroundColor: '#FFFFFF',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#DDE3F0',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#C3CCE2',
  },
  '& .MuiSelect-select': {
    paddingTop: '7px',
    paddingBottom: '7px',
  },
};

/** `1,406` rather than `1406`. */
export function formatCount(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString() : '0';
}

/** `Harshana Kodagoda` -> `HK` */
export function getInitials(firstName?: string, lastName?: string): string {
  return `${firstName?.trim()?.[0] ?? ''}${lastName?.trim()?.[0] ?? ''}`
    .toUpperCase()
    .trim();
}

/**
 * `2.3 days` / `14 days` / `1.4 hours`. Used by the Needs attention card and
 * the job health rows where a duration reads better than a raw timestamp.
 */
export function formatDuration(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '0 hours';
  if (hours < 24) {
    const rounded = Math.round(hours * 10) / 10;
    return `${rounded} hour${rounded === 1 ? '' : 's'}`;
  }
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}
